import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import type { QixitAuthApi, QixitAuthAttribution, QixitAuthContext } from './types';

export type SocialContinueSuccess = {
  accessToken: string;
  refreshToken?: string;
  userId: string;
  username: string;
};

type Provider = 'google' | 'microsoft';

type Props = {
  provider: Provider;
  api: QixitAuthApi;
  context: QixitAuthContext;
  attribution: QixitAuthAttribution;
  returnUrl?: string;
  compact?: boolean;
  /** Share a row with the other provider instead of taking the full width. */
  fill?: boolean;
  onSuccess: (result: SocialContinueSuccess) => void;
};

type Status = { enabled?: boolean; useDevStub?: boolean };

const LABELS: Record<Provider, string> = { google: 'Google', microsoft: 'Microsoft' };

function buildStartUrl(
  provider: Provider,
  api: QixitAuthApi,
  context: QixitAuthContext,
  attribution: QixitAuthAttribution,
  returnUrl: string,
  extra: Record<string, string>
): string {
  const qs = new URLSearchParams({
    acceptTerms: 'true',
    // Mailbox sessions need full scope; checkout sessions stay micropay-scoped.
    loginContext: context === 'mailbox' ? 'mailbox' : 'micropay',
    checkoutContext: context,
    openerOrigin: window.location.origin,
    returnUrl,
    ...extra,
  });
  if (attribution.sourcePage) qs.set('sourcePage', attribution.sourcePage);
  if (attribution.referralCode?.trim()) qs.set('referralCode', attribution.referralCode.trim());
  if (attribution.payoutId) qs.set('payoutId', attribution.payoutId);
  if (attribution.productId) qs.set('productId', attribution.productId);
  return `${api.apiBase.replace(/\/$/, '')}/api/Auth/${provider}?${qs.toString()}`;
}

/** Popup sign-in with a one-time handoff id; tokens are redeemed over XHR, never posted between windows. */
export default function SocialContinueButton({
  provider,
  api,
  context,
  attribution,
  returnUrl,
  compact = true,
  fill = false,
  onSuccess,
}: Props) {
  const [enabled, setEnabled] = useState(false);
  const [useDevStub, setUseDevStub] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stubEmail, setStubEmail] = useState('');
  const label = LABELS[provider];
  const fontSize = compact ? '11px' : '14px';

  useEffect(() => {
    api
      .get<Status>(`/api/Auth/${provider}/status`)
      .then((data) => {
        setEnabled(!!data?.enabled);
        setUseDevStub(provider === 'google' && !!data?.useDevStub);
      })
      .catch(() => setEnabled(false));
  }, [api, provider]);

  const destination = returnUrl || (typeof window !== 'undefined' ? window.location.href : '');

  const start = useCallback(() => {
    setError(null);
    setBusy(true);
    const extra: Record<string, string> = { deviceId: api.getDeviceId() };
    if (useDevStub) extra.stubEmail = stubEmail.trim() || `ggl-${Date.now()}@example.com`;
    const startUrl = buildStartUrl(provider, api, context, attribution, destination, extra);
    const popup = window.open(startUrl, `qixit_${provider}_auth`, 'width=520,height=640');
    if (!popup) {
      setBusy(false);
      setError(`Popup blocked. Allow popups for this site, or open ${label} sign-in in a new tab.`);
      return;
    }

    const apiOrigin = new URL(api.apiBase || window.location.origin, window.location.origin).origin;
    const allowedOrigins = new Set([
      apiOrigin,
      window.location.origin,
      'http://127.0.0.1:5220',
      'http://localhost:5220',
    ]);

    let settled = false;
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(closedPoll);
    };

    const onMessage = async (event: MessageEvent) => {
      if (!allowedOrigins.has(event.origin)) return;
      const data = event.data;
      if (!data || !data.handoffId) return;
      const matches =
        provider === 'google'
          ? data.type === 'googleAuth' || (data.type === 'socialAuth' && data.provider === 'google')
          : data.type === 'socialAuth' && data.provider === 'microsoft';
      if (!matches || settled) return;
      settled = true;
      cleanup();
      try {
        const handoff = await api.get<SocialContinueSuccess>(
          `/api/Auth/social-handoff/${encodeURIComponent(data.handoffId)}`
        );
        if (!handoff?.accessToken || !handoff.userId || !handoff.username) {
          throw new Error('Invalid handoff response');
        }
        onSuccess({
          accessToken: handoff.accessToken,
          refreshToken: handoff.refreshToken,
          userId: handoff.userId,
          username: handoff.username,
        });
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : `${label} sign-in failed`);
      } finally {
        setBusy(false);
        try {
          popup.close();
        } catch {
          /* ignore */
        }
      }
    };

    window.addEventListener('message', onMessage);
    const closedPoll = window.setInterval(() => {
      if (popup.closed && !settled) {
        settled = true;
        cleanup();
        setBusy(false);
      }
    }, 500);
  }, [api, attribution, context, destination, label, onSuccess, provider, stubEmail, useDevStub]);

  if (!enabled) return null;

  // The popup-blocked fallback must carry the same attribution, or that signup loses its referrer.
  const fallbackUrl = buildStartUrl(provider, api, context, attribution, destination, {});

  return (
    <Box sx={{ flex: fill ? 1 : undefined, width: fill ? undefined : '100%', minWidth: 0, mb: fill ? 0 : 0.75 }} data-testid={`${provider}-continue`}>
      <Tooltip title={`Continue with ${label}`} arrow>
        <Box component="span" sx={{ display: 'block', width: '100%' }}>
          <Button
            variant="outlined"
            fullWidth
            size={compact ? 'small' : 'medium'}
            disabled={busy}
            onClick={start}
            aria-label={busy ? `Waiting for ${label}` : `Continue with ${label}`}
            sx={{ fontSize, padding: compact ? '4px' : undefined, textTransform: 'none' }}
            data-testid={`${provider}-continue-button`}
          >
            {busy ? 'Waiting…' : label}
          </Button>
        </Box>
      </Tooltip>
      {useDevStub && (
        <input
          type="email"
          placeholder="Dev stub email"
          value={stubEmail}
          onChange={(e) => setStubEmail(e.target.value)}
          data-testid="google-stub-email"
          style={{ width: '100%', fontSize, marginTop: 4, padding: 4, boxSizing: 'border-box' }}
        />
      )}
      {error && (
        <Typography color="error" sx={{ fontSize: compact ? '10px' : '13px', mt: 0.5 }} data-testid={`${provider}-continue-error`}>
          {error}{' '}
          <a href={fallbackUrl} target="_blank" rel="noreferrer">
            Open {label} sign-in
          </a>
        </Typography>
      )}
    </Box>
  );
}
